import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { SUPER_ADMIN_EMAIL } from '../constants';
import { storage } from '../utils/storage';

const ProtectedRoute = ({ children }) => {
  const [user, loading, error] = useAuthState(auth);
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authCheckLoading, setAuthCheckLoading] = useState(true);
  const params = useParams();

  useEffect(() => {
    const checkAuthorization = async () => {
      if (loading) {
        return;
      }

      if (error || !user) {
        setIsAuthorized(false);
        setAuthCheckLoading(false);
        return;
      }

      // Check if user is super admin
      const isSuperAdmin = user.email === SUPER_ADMIN_EMAIL;
      if (isSuperAdmin) {
        setIsAuthorized(true);
        setAuthCheckLoading(false);
        return;
      }

      // Handle direct access to /live-order/vendorId
      const isLiveOrderRoute = location.pathname.startsWith('/live-order/');
      const isOrderHistoryRoute = location.pathname === '/order-history';
      const isEditOrderRoute = location.pathname.startsWith('/edit-order/');
      const requiresVendorDetailsAccess = isLiveOrderRoute || isOrderHistoryRoute || isEditOrderRoute;

      if (requiresVendorDetailsAccess) {
        try {
          let vendorId = params.vendorId;
          
          // If no vendorId in params, check all vendors
          if (!vendorId) {
            const vendorsRef = collection(db, 'vendors');
            const vendorsSnapshot = await getDocs(vendorsRef);

            let found = false;
            for (const vendorDoc of vendorsSnapshot.docs) {
              const currentVendorId = vendorDoc.id;
              const vendorDetailsRef = doc(db, 'vendors', currentVendorId, 'vendorDetails', 'details');
              const vendorDetailsSnap = await getDoc(vendorDetailsRef);

              if (vendorDetailsSnap.exists()) {
                const vendorDetails = vendorDetailsSnap.data();
                const primaryEmail = vendorDetails.email || '';

                if (primaryEmail === user.email) {
                  found = true;
                  vendorId = currentVendorId;
                  storage.set(`vendor_auth_${user.email}`, { id: currentVendorId, name: primaryEmail });
                  break;
                }

                const authorizedEmails = vendorDetails.authorizedEmails || [];
                for (const authEmail of authorizedEmails) {
                  const emailToCheck = typeof authEmail === 'object' ? authEmail.email : authEmail;
                  if (emailToCheck === user.email) {
                    found = true;
                    vendorId = currentVendorId;
                    storage.set(`vendor_auth_${user.email}`, { id: currentVendorId, name: emailToCheck });
                    break;
                  }
                }

                if (found) break;
              }
            }
            setIsAuthorized(found);
          } else {
            // Check specific vendorId from URL
            const vendorDetailsRef = doc(db, 'vendors', vendorId, 'vendorDetails', 'details');
            const vendorDetailsSnap = await getDoc(vendorDetailsRef);

            if (vendorDetailsSnap.exists()) {
              const vendorDetails = vendorDetailsSnap.data();
              const primaryEmail = vendorDetails.email || '';
              const authorizedEmails = vendorDetails.authorizedEmails || [];
              
              const isAuthorizedEmail = primaryEmail === user.email || 
                authorizedEmails.some(authEmail => {
                  const emailToCheck = typeof authEmail === 'object' ? authEmail.email : authEmail;
                  return emailToCheck === user.email;
                });

              if (isAuthorizedEmail) {
                storage.set(`vendor_auth_${user.email}`, { id: vendorId, name: primaryEmail });
              }
              setIsAuthorized(isAuthorizedEmail);
            } else {
              setIsAuthorized(false);
            }
          }
        } catch (err) {
          console.error('Vendor details authorization check failed:', err);
          setIsAuthorized(false);
        } finally {
          setAuthCheckLoading(false);
        }
        return;
      }

      // For other protected routes, check authorizedEmails in the root vendors collection
      const cachedVendor = storage.get(`vendor_auth_${user.email}`);
      if (cachedVendor) {
        setIsAuthorized(true);
        setAuthCheckLoading(false);
        return;
      }

      try {
        const vendorsRef = collection(db, 'vendors');
        const q = query(vendorsRef, where('authorizedEmails', 'array-contains', user.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty()) {
          const vendorData = querySnapshot.docs[0].data();
          storage.set(`vendor_auth_${user.email}`, { id: querySnapshot.docs[0].id, name: vendorData.name });
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
        }
      } catch (err) {
        console.error('Authorization check failed:', err);
        setIsAuthorized(false);
      } finally {
        setAuthCheckLoading(false);
      }
    };

    checkAuthorization();
  }, [user, loading, error, location.pathname, params.vendorId]);

  if (loading || authCheckLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (error || !user || !isAuthorized) {
    // Store the attempted URL to redirect back after login
    const returnUrl = location.pathname + location.search;
    return <Navigate to="/vendor-login" state={{ from: returnUrl }} replace />;
  }

  return children;
};

export default ProtectedRoute;